import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { AuthOrgProvider } from "./context/AuthOrgContext";
import { LanguageProvider } from "./context/LanguageContext";
import { PublicDataProvider } from "./context/PublicDataContext";
import { MetaSetupProvider } from "./context/MetaSetupContext";
import PrivateRoute from "./components/PrivateRoute";
import RoleGuard from "./components/RoleGuard";
import { RolePermission } from "./utils/roles";
import PrivateLayout from "./components/layout/PrivateLayout";
import Login from "./pages/Login";
import Homepage from "./pages/Homepage";
import "./assets/css/index.css";
import { BreadcrumbsProvider } from "./context/BreadcrumbContext";
import AccountSetup from "./pages/setup/AccountSetup";
import { message } from "antd";
import ErrorPage from "./pages/setup/error/Error";
import CampaignListing from "./pages/MetaCampaignListing";
import AdSetListing from "./pages/MetaAdSetListing";
import AdListing from "./pages/MetaAdListing";
import MetaAnalyticsDashboard from "./pages/MetaAnalytics";
import BotAnalyticsPage from "./pages/BotAnalytics";
import MetaConversionEvents from "./pages/MetaConversionEvents";
import UserDetails from "./pages/UserDetails";
import Logout from "./pages/Logout";
import MetaRetargeting from "./pages/Retargeting";
import { Audience } from './pages/Audience';
import CalculatedAudience from './pages/CalculatedAudience';
import MetaCampaignManagementGrid from "./pages/MetaCampaignManagementGrid";
import MetaCampaignCreation from "./pages/MetaCampaignCreation";
import VerismartCampaignCreation from "./pages/VerismartCampaignCreation"
import VerismartAudienceListing from "./pages/VerismartAudienceListing";
import UserOnboarding from "./pages/verismartSetup/VerismartSetup";
import Setting from './pages/Setting'
import SourceMapping from "./pages/SourceMapping";
import CertLogin from "./pages/CredLogin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import UserManagement from "./pages/settings/UserManagement";
import OrgManagement from "./pages/settings/OrgManagement";
import Leads from "./pages/Leads";
import LeadConfiguration from "./pages/settings/LeadConfiguration";
import OfflineStatusUpload from "./pages/OfflineStatusUpload";
import BillingReport from "./pages/settings/BillingReport";
import ExcludeNumber from "./pages/settings/ExcludeNumber";
import PlatformBudgetConfig from "./pages/settings/PlatformBudgetConfig";
import LeadDistribution from "./pages/settings/LeadDistribution";
// import MonitoringDiagnostics from "./pages/MonitoringDiagnostics";
// import MonitoringRouteTracker from "./monitoring/MonitoringRouteTracker";
message.config({
  top: 20,
  duration: 2,
  maxCount: 2,
});
const App: React.FC = () => {
  const withPermission = (permission: RolePermission, element: React.ReactElement) => (
    <RoleGuard permission={permission}>{element}</RoleGuard>
  );

  return (
    <AuthOrgProvider>
      <MetaSetupProvider>
        <LanguageProvider>
          <PublicDataProvider>
            <BreadcrumbsProvider>
              <Router basename="/admanager/v2">
                {/* <MonitoringRouteTracker /> */}
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/user-login" element={<CertLogin />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/logout" element={<><Logout /></>} />
                  {/* <Route path="/register" element={<Register />} /> */}


                  <Route element={<PrivateRoute />}>
                    <Route element={<PrivateLayout />}>
                      <Route path="/error" element={<ErrorPage />} />
                      <Route path="/home" element={withPermission('viewHome', <Homepage />)} />
                      <Route path="/setup" element={withPermission('accessSetup', <AccountSetup />)} />
                      <Route path="/ctc-ads-meta/ad-accounts" element={withPermission('viewMetaAdAccounts', <CampaignListing />)} />
                      <Route path="/ctc-ads-meta/ad-accounts/adsets/:id" element={withPermission('viewMetaAdAccounts', <AdSetListing />)} />
                      <Route path="/ctc-ads-meta/ad-accounts/ads/:id" element={withPermission('viewMetaAdAccounts', <AdListing />)} />
                      <Route path="/ctc-ads-meta/ad-accounts/adset/ads/:id" element={withPermission('viewMetaAdAccounts', <AdListing />)} />
                      {/* <Route path="/ctc-ads-meta/ad/:id" element={<><AdPreview /></>} /> */}
                      <Route path="/ctc-ads-meta/ad-analytics" element={withPermission('viewAdAnalytics', <MetaAnalyticsDashboard />)} />
                      <Route path="/ctc-ads-meta/bot-analytics" element={withPermission('viewBotAnalytics', <BotAnalyticsPage />)} />
                      <Route path="/ctc-ads-meta/conversion-events" element={withPermission('manageConversionEvents', <MetaConversionEvents />)} />
                      <Route path="/ctc-ads-meta/retargeting" element={withPermission('manageRetargeting', <MetaRetargeting />)} />
                      {/* <Route path="/campaign-management" element={<CampaignManagementListing />} /> */}
                      <Route path="/campaign-management" element={withPermission('viewCampaignDashboard', <MetaCampaignManagementGrid />)} />
                      <Route path="/verismart-setup" element={withPermission('accessSetup', <UserOnboarding />)} />
                      <Route path="/campaign/create" element={withPermission('viewCampaignDashboard', <VerismartCampaignCreation />)} />
                      <Route path="/campaign-management/create-campaign" element={withPermission('viewCampaignDashboard', <MetaCampaignCreation />)} />
                      <Route path="/audience" element={withPermission('viewAudience', <Audience />)} />
                      <Route path="/calculatedAudience" element={withPermission('viewAudience', <CalculatedAudience />)} />
                      <Route path="/help" element={<></>} />
                      <Route path="/customAudience" element={withPermission('viewAudience', <VerismartAudienceListing />)} />
                      <Route path="/settings" element={withPermission('viewSettings', <Setting />)} />
                      {/* <Route path="/settings" element={<></>} /> */}
                      <Route path="/settings/create-source-mapping" element={withPermission('viewSettings', <SourceMapping />)} />
                      <Route path="/settings/user-details" element={<UserDetails />} />
                      <Route path="/setting/users" element={withPermission('viewConfig', <UserManagement />)} />
                      <Route path="/setting/organizations" element={withPermission('viewConfig', <OrgManagement />)} />
                      <Route path="/leads" element={withPermission('viewLeads', <Leads />)} />
                      <Route path="/settings/lead-configuration" element={withPermission('viewSettings', <LeadConfiguration />)} />
                      <Route path="/billing" element={withPermission('viewBillingReport', <BillingReport />)} />
                      <Route path="/offline-status-upload" element={withPermission('viewSettings', <OfflineStatusUpload />)} />
                      <Route path="/settings/exclude-number" element={withPermission('viewSettings', <ExcludeNumber />)} />
                      <Route path="/settings/platform-budget" element={withPermission('viewPlatformBudget', <PlatformBudgetConfig />)} />
                      <Route path="/settings/lead-distribution" element={withPermission('manageLeadDistribution', <LeadDistribution />)} />
                      {/* <Route path="/diagnostics" element={withPermission('viewSettings', <MonitoringDiagnostics />)} /> */}
                    </Route>
                  </Route>
                  <Route path="/" element={<Navigate replace to="/user-login" />} />
                </Routes>
              </Router>
            </BreadcrumbsProvider>
          </PublicDataProvider>
        </LanguageProvider>
      </MetaSetupProvider>
    </AuthOrgProvider>
  );
};

export default App;
